export const normalizeTimeValue = (timeValue) => {
    if (!timeValue) {
        return '';
    }

    const value = String(timeValue).trim();

    if (/^\d{2}:\d{2}$/.test(value)) {
        return value;
    }

    if (/^\d{2}:\d{2}:\d{2}$/.test(value)) {
        return value.substring(0, 5);
    }

    return value;
};

export const buildTimeValue = (hour, minute) =>
    `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

export const snapMinuteToInterval = (minute, intervalMinutes = 5) => {
    const snapped = Math.round(minute / intervalMinutes) * intervalMinutes;
    return snapped === 60 ? 0 : snapped;
};

export const parseTimeParts = (timeValue, intervalMinutes = 5) => {
    const normalized = normalizeTimeValue(timeValue);

    if (!normalized) {
        return { hour: 9, minute: 0 };
    }

    const [hour, minute] = normalized.split(':').map(Number);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return { hour: 9, minute: 0 };
    }

    let totalMinutes = hour * 60 + minute;
    totalMinutes = Math.round(totalMinutes / intervalMinutes) * intervalMinutes;

    return {
        hour: Math.floor(totalMinutes / 60) % 24,
        minute: totalMinutes % 60,
    };
};

export const normalizeTimeToInterval = (timeValue, intervalMinutes = 5) => {
    const normalized = normalizeTimeValue(timeValue);

    if (!normalized) {
        return '';
    }

    const { hour, minute } = parseTimeParts(normalized, intervalMinutes);
    return buildTimeValue(hour, minute);
};

export const generateHourOptions = () => Array.from({ length: 24 }, (_, hour) => hour);

export const generateMinuteOptions = (intervalMinutes = 5) =>
    Array.from({ length: 60 / intervalMinutes }, (_, index) => index * intervalMinutes);

export const formatHourLabel = (hour) => {
    const period = hour < 12 ? 'AM' : 'PM';
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12} ${period}`;
};

export const formatMinuteLabel = (minute) => minute.toString().padStart(2, '0');

export const formatDisplayTime = (timeValue, { use12Hour = true } = {}) => {
    const normalized = normalizeTimeValue(timeValue);

    if (!normalized) {
        return '';
    }

    const [hours, minutes] = normalized.split(':').map(Number);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return normalized;
    }

    if (!use12Hour) {
        return normalized;
    }

    const date = new Date(2000, 0, 1, hours, minutes);
    return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};

const readFormatPart = (parts, type) => {
    const match = parts.find((part) => part.type === type);
    return match ? Number(match.value) : NaN;
};

const readFormatText = (parts, type) => {
    const match = parts.find((part) => part.type === type);
    return match?.value || '';
};

/**
 * Current (or provided) instant broken into calendar/time fields for a clinic IANA zone id.
 * Falls back to the browser local zone when zoneId is missing/invalid.
 */
export const getZonedDateTimeParts = (zoneId, date = new Date()) => {
    const fallback = () => ({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        hour: date.getHours(),
        minute: date.getMinutes(),
        totalMinutes: date.getHours() * 60 + date.getMinutes(),
    });

    const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    };

    if (zoneId) {
        options.timeZone = zoneId;
    }

    try {
        const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);

        const year = readFormatPart(parts, 'year');
        const month = readFormatPart(parts, 'month');
        const day = readFormatPart(parts, 'day');
        let hour = readFormatPart(parts, 'hour');
        const minute = readFormatPart(parts, 'minute');
        const dayPeriod = readFormatText(parts, 'dayPeriod').toUpperCase();

        if ([year, month, day, hour, minute].some((value) => Number.isNaN(value))) {
            return fallback();
        }

        if (dayPeriod.startsWith('P') && hour !== 12) {
            hour += 12;
        }

        if (dayPeriod.startsWith('A') && hour === 12) {
            hour = 0;
        }

        return {
            year,
            month,
            day,
            hour,
            minute,
            totalMinutes: hour * 60 + minute,
        };
    } catch {
        return fallback();
    }
};

export const isSameZonedCalendarDay = (date, zoneId, referenceDate = new Date()) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return false;
    }

    const zoned = getZonedDateTimeParts(zoneId, referenceDate);

    return (
        date.getFullYear() === zoned.year &&
        date.getMonth() + 1 === zoned.month &&
        date.getDate() === zoned.day
    );
};
